/**
 * recentLessons — tracks the last few lessons the teacher opened so the
 * in-page top bar can surface quick "jump back" pills.
 *
 * Why localStorage instead of a server table?
 *   - The list is per-device and ephemeral. A teacher checking lessons
 *     on a chromebook in the lab doesn't need that history to follow
 *     them home to their iPad.
 *   - Zero schema change, zero round-trip latency.
 *
 * Shape:
 *   [{id, name, ts}]  — newest-first, max 5 entries, deduped by id.
 */

const STORAGE_KEY = "ae:recent-lessons";
const MAX_ENTRIES = 7;
const EVENT_NAME = "ae:recent-lessons:changed";

const _safeRead = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((e) => e && typeof e.id === "string" && typeof e.name === "string")
      .slice(0, MAX_ENTRIES);
  } catch {
    return [];
  }
};

const _safeWrite = (list) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    // Notify same-tab subscribers; `storage` event only fires cross-tab.
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch {
    /* localStorage unavailable — silently no-op */
  }
};

export const getRecentLessons = () => _safeRead();

/**
 * Push a lesson to the front of the recents list. Falsy id or
 * "Untitled lesson"-style placeholders are ignored so we don't pin
 * empty pills.
 */
export const pushRecentLesson = (id, name) => {
  if (!id || typeof id !== "string") return;
  const cleanName = (name || "").trim();
  if (!cleanName) return;
  const now = Date.now();
  const list = _safeRead().filter((e) => e.id !== id);
  list.unshift({ id, name: cleanName, ts: now });
  _safeWrite(list.slice(0, MAX_ENTRIES));
};

/**
 * Remove a lesson from the recents list — used by the LessonWorkspace
 * delete flow so the pill disappears the moment the underlying lesson
 * is gone.
 */
export const removeRecentLesson = (id) => {
  if (!id) return;
  const next = _safeRead().filter((e) => e.id !== id);
  _safeWrite(next);
};

export const RECENT_LESSONS_EVENT = EVENT_NAME;
